import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { useDataModel } from '@/hooks/useDataModel';
import { logger } from '@/services/logger';
import { DataModelLayout } from '@/types/DataModel';

interface LayoutContextInterface {
    layout?: DataModelLayout;
}

const LayoutContext = createContext<LayoutContextInterface | undefined>(undefined);

const useLayout = () => {
    const layoutContext = useContext(LayoutContext);

    if (!layoutContext) {
        throw new Error('useLayout must be used within an LayoutContext');
    }

    return layoutContext;
};

const LayoutProvider = ({ layoutName, children }: PropsWithChildren & { layoutName: string }) => {

    const { dataModel } = useDataModel();
    // Lazy-init from the data model when it's already available (the common
    // case, e.g. when a grid row opens an inline editor). This avoids a
    // spurious render pass showing the 80vh loader before the effect below
    // resolves the layout.
    const [layout, setLayout] = useState<DataModelLayout | undefined>(
        () => dataModel?.layout(layoutName),
    );

    useEffect(() => {
        const layout = dataModel?.layout(layoutName);

        if (!layout) {
            logger.error(`LayoutProvider: Could not load layout ${layoutName}`);
        } else {
            setLayout(layout);
        }

        //eslint-disable-next-line
    }, [dataModel]);

    if (!layout) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-max-h-[80vh] tw-min-h-[80vh]">
                <Loader />;
            </div>
        );
    } else {
        return <LayoutContext.Provider value={{ layout }}>{children}</LayoutContext.Provider>;
    }

};

/** Provides an already resolved layout, e.g. an asset layout that `dataModel.layout(name)` cannot look up. */
const ResolvedLayoutProvider = ({ layout, children }: PropsWithChildren & { layout: DataModelLayout }) => {
    const value = useMemo(() => ({ layout }), [layout]);
    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export { LayoutProvider, ResolvedLayoutProvider, useLayout };
